import type { AnimalQuery, AnimalRead } from '@pedigreesmart/api-client'
import { sexLabel } from '@pedigreesmart/core'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { useSession, useTerm } from '../session/SessionProvider.js'
import './animals.css'

const PER_PAGINA = 25

/**
 * L'elenco dei soggetti: la schermata da cui si passa più tempo.
 *
 * Tre scelte che nascono da come si lavora in segreteria, non da come si
 * disegna un'interfaccia.
 *
 * **La ricerca non ha un pulsante «cerca».** Chi ha in mano un cane e legge un
 * microchip lo digita e guarda: un pulsante in più è un gesto in più, ripetuto
 * trecento volte.
 *
 * **Il risultato precedente resta visibile mentre arriva il nuovo**
 * (`keepPreviousData`). Senza, ogni tasto premuto svuota la tabella e la
 * pagina sobbalza — e chi corregge una cifra perde il riferimento visivo.
 *
 * **Le colonne parlano la lingua della specie.** L'intestazione dice
 * «Fattrice» a un'associazione cinofila e «Fattrice» a una equestre, ma
 * «Cucciolata» diventa «Parto»: le parole vengono dal contesto di sessione,
 * non da costanti scritte qui.
 */
export function AnimalList({
  onApri,
  onNuovo,
}: {
  onApri?: ((id: number) => void) | undefined
  onNuovo?: (() => void) | undefined
}) {
  const { resources, species } = useSession()
  const [cerca, setCerca] = useState('')
  const [pagina, setPagina] = useState(1)

  const animali = useTerm('animal.plural')

  const query = useQuery({
    queryKey: ['animals', species?.code, cerca, pagina],
    queryFn: ({ signal }) => {
      // Le chiavi assenti non si mandano come `undefined`: `?species=undefined`
      // è una stringa, e il backend la userebbe come filtro trovando nulla.
      const filtri: AnimalQuery = { page: pagina, items_per_page: PER_PAGINA }

      if (species) filtri['species.code'] = species.code
      if (cerca.trim()) filtri.q = cerca.trim()

      return resources.animals.list(filtri, signal)
    },
    placeholderData: keepPreviousData,
  })

  const pagine = query.data ? Math.max(1, Math.ceil(query.data.totalItems / PER_PAGINA)) : 1

  return (
    <section aria-labelledby="titolo-animali">
      <div className="elenco__testa">
        <h1 id="titolo-animali">{animali}</h1>
        <div className="elenco__azioni">
          {query.data && (
            <p className="elenco__conteggio" aria-live="polite">
              {query.data.totalItems === 1
                ? '1 soggetto'
                : `${query.data.totalItems.toLocaleString('it-IT')} soggetti`}
            </p>
          )}
          {onNuovo && (
            <button type="button" onClick={onNuovo}>
              Nuovo
            </button>
          )}
        </div>
      </div>

      <label className="elenco__ricerca">
        <span className="solo-lettore">Cerca per nome, numero di registro o microchip</span>
        <input
          type="search"
          value={cerca}
          placeholder="Nome, numero di registro o microchip"
          onChange={(event) => {
            setCerca(event.target.value)
            // Cambiando la ricerca si torna alla prima pagina: restare alla
            // sesta di un elenco che ora ne ha due mostrerebbe il vuoto.
            setPagina(1)
          }}
        />
      </label>

      {query.isError && (
        <p role="alert" className="messaggio messaggio--errore">
          Non è stato possibile leggere l'elenco. {descriviErrore(query.error)}
        </p>
      )}

      {query.isPending && <p className="messaggio">Caricamento…</p>}

      {query.data && query.data.member.length === 0 && (
        <p className="messaggio">
          {cerca.trim()
            ? `Nessun risultato per «${cerca.trim()}».`
            : `Nessun soggetto ancora registrato.`}
        </p>
      )}

      {query.data && query.data.member.length > 0 && (
        <div className="tabella-scorrevole" data-attesa={query.isFetching ? 'si' : undefined}>
          <table>
            <thead>
              <tr>
                <th scope="col">Nome</th>
                <th scope="col">Numero di registro</th>
                <th scope="col">Sesso</th>
                <th scope="col">Nascita</th>
                <th scope="col">Razza</th>
                <th scope="col">Proprietario</th>
              </tr>
            </thead>
            <tbody>
              {query.data.member.map((animale) => (
                <Riga key={animale.id} animale={animale} onApri={onApri} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagine > 1 && (
        <nav className="paginazione" aria-label="Pagine">
          <button
            type="button"
            data-variante="secondario"
            disabled={pagina <= 1}
            onClick={() => {
              setPagina((p) => p - 1)
            }}
          >
            Precedente
          </button>
          <span aria-live="polite">
            Pagina {pagina} di {pagine}
          </span>
          <button
            type="button"
            data-variante="secondario"
            disabled={pagina >= pagine}
            onClick={() => {
              setPagina((p) => p + 1)
            }}
          >
            Successiva
          </button>
        </nav>
      )}
    </section>
  )
}

function Riga({
  animale,
  onApri,
}: {
  animale: AnimalRead
  onApri?: ((id: number) => void) | undefined
}) {
  // Le parole del sesso vengono dalla terminologia della specie: alcune non
  // dicono «maschio» e «femmina» ma hanno nomi propri.
  const { species } = useSession()
  const apribile = typeof onApri === 'function'

  return (
    <tr
      className={apribile ? 'riga--apribile' : undefined}
      onClick={
        apribile
          ? () => {
              onApri(animale.id)
            }
          : undefined
      }
    >
      <td>
        {/* Il nome completo porta l'affisso e va mostrato quando c'è: è il
            nome con cui il soggetto compare sul certificato. */}
        <span className="riga__nome">{animale.full_name ?? animale.name}</span>
        {animale.full_name && animale.full_name !== animale.name && (
          <span className="riga__secondario">{animale.name}</span>
        )}
      </td>
      <td className="codice">{animale.registry_number ?? '—'}</td>
      <td>{sexLabel(animale.sex, species)}</td>
      <td>{data(animale.birth_date)}</td>
      <td>{animale.breed?.name ?? '—'}</td>
      <td>{animale.current_owner?.display_name ?? '—'}</td>
    </tr>
  )
}

function data(iso: string | null | undefined): string {
  if (!iso) return '—'

  const quando = new Date(iso)

  return Number.isNaN(quando.getTime()) ? '—' : quando.toLocaleDateString('it-IT')
}

function descriviErrore(errore: unknown): string {
  const stato = (errore as { status?: number } | null)?.status

  if (stato === 403) return 'Il vostro profilo non ha accesso a questo elenco.'
  if (stato === 401) return 'La sessione è scaduta: rientrare.'

  return 'Riprovare fra qualche istante.'
}
